'use client';

import { useEffect, useRef } from 'react';
import { useUser } from '@clerk/nextjs';
import posthog from 'posthog-js';

export function PostHogIdentify() {
  const { isSignedIn, user } = useUser();
  const previouslySignedIn = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    if (isSignedIn && user) {
      // Tier flows from Clerk publicMetadata (set by Stripe / Apple webhooks).
      // Falling back to 'free' matches the server default for new portfolios.
      const tier = (user.publicMetadata?.tier as string | undefined) ?? 'free';

      // Always re-identify when `user` changes — this catches tier upgrades
      // mid-session once Clerk publicMetadata refreshes. `identify` is idempotent
      // when the distinct_id and properties are unchanged.
      posthog.identify(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        tier,
      });

      // Only fire sign-up / sign-in on auth-state transitions, not on every
      // metadata refresh — otherwise an upgrade would re-emit `user_signed_in`.
      if (previouslySignedIn.current !== isSignedIn) {
        const createdAt = user.createdAt ? new Date(user.createdAt).getTime() : 0;
        const isNewUser = Date.now() - createdAt < 60000;
        posthog.capture(isNewUser ? 'user_signed_up' : 'user_signed_in');
      }
    } else if (previouslySignedIn.current === true && !isSignedIn) {
      // User signed out
      posthog.reset();
    }

    previouslySignedIn.current = isSignedIn;
  }, [isSignedIn, user]);

  return null;
}
